import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Flex, HistoryOutline, rem, StyleProps, Text, Tooltip } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';

import { GroupAvatar, isGroupAvatarType } from '../../../groups/group-avatar';

interface RecipientOptionProps extends StyleProps {
	recipient: Recipient;
	showNoOtherUsersHint?: boolean;
}

export const RecipientOption: FC<RecipientOptionProps> = ({ recipient, showNoOtherUsersHint = false, ...rest }: RecipientOptionProps) => {
	const { t } = useTranslation(['components', 'recognition']);
	const isGroup = recipient.type === 'group';
	const isEmail = recipient.type === 'email';
	const isEveryone = recipient.dimension === 'everyone';
	const groupTypeText = t(`group_popover.${recipient.dimension}`);
	const hasDepartment = !!recipient.department;
	const isPendingInvitation = recipient.type === 'username' && recipient.pendingInvitation;

	return (
		<Flex {...rest}>
			{isGroup && isGroupAvatarType(recipient.dimension) ? (
				<GroupAvatar groupType={recipient.dimension} mr={3} w={rem(44)} h={rem(44)} minW={rem(44)} size="lg" />
			) : (
				<Avatar
					name={recipient.displayName}
					mr={3}
					size="lg"
					boxSize={rem(44)}
					src={recipient.profilePic}
					hideOverlay
					{...(isGroup && { bg: 'transparent' })}
				/>
			)}

			<Box flex={1} minWidth={0} textAlign="left">
				<Text fontSize="md" fontWeight="medium" lineHeight={6} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{recipient.displayName}
				</Text>

				{showNoOtherUsersHint ? (
					<Box>
						<Text color="text.softest" fontSize="sm" fontWeight="normal" lineHeight={5}>
							@{recipient.username}
						</Text>
						<Text color="text.default" fontSize="sm" fontWeight="medium" lineHeight={5}>
							{t('rich_media.search_users.no_other_users')}
						</Text>
						<Text color="text.softest" fontSize="sm" fontWeight="normal" lineHeight={5}>
							{t('rich_media.search_users.enter_work_email_to_invite_new_users')}
						</Text>
					</Box>
				) : (
					<Text color="text.softest" fontSize="sm" fontWeight="normal" lineHeight={5} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
						{isGroup ? (
							<>
								{isEveryone ? '' : `${groupTypeText} • `}
								{recipient.userIds?.length} {t('people')}
							</>
						) : isEmail ? (
							t('bonus_input.search_users.enter_email_to_invite', { ns: 'recognition' })
						) : (
							<>
								@{recipient.username}
								{hasDepartment && (
									<Text as="span" textTransform="capitalize" pl={1} fontSize="sm" color="text.softest">
										• {recipient.department}
									</Text>
								)}
							</>
						)}
					</Text>
				)}
			</Box>
			{isPendingInvitation && (
				<Tooltip label={t('rich_media.search_users.pending_invite', { defaultValue: 'Pending invite' })} placement="top">
					<Box
						as="span"
						aria-label={t('rich_media.search_users.pending_invite', { defaultValue: 'Pending invite' })}
						color="text.softest"
						display="inline-flex"
						alignItems="center"
						flexShrink={0}
						ml={2}
					>
						<HistoryOutline size="sm" />
					</Box>
				</Tooltip>
			)}
		</Flex>
	);
};
